<template>
  <div v-if="open" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="modal-title-section">
          <h3>Edit Contacts</h3>
          <div v-if="ownerName" class="owner-info">
            <span class="owner-label">Owner:</span>
            <span class="owner-name">{{ ownerName }}</span>
          </div>
        </div>
        <button class="close-button" @click="closeModal">
          <div class="close-icon"></div>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Tab Navigation -->
        <div class="tab-navigation">
          <button 
            class="tab-button" 
            :class="{ 'active': activeTab === 'individuals' }"
            @click="activeTab = 'individuals'"
          >
            Individuals
          </button>
          <button 
            class="tab-button" 
            :class="{ 'active': activeTab === 'contacts' }"
            @click="activeTab = 'contacts'"
          >
            Contacts
          </button>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <!-- Individuals Tab -->
          <div v-if="activeTab === 'individuals'" class="tab-panel">
            <div class="contacts-header">
              <h4>Individuals</h4>
            </div>
        
            <div v-if="loading" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading individuals...</p>
            </div>
            
            <div v-else class="contacts-list">
              <div v-if="filteredContacts.length === 0" class="no-contacts">
                <p>No individuals found.</p>
              </div>
              <div v-else>
                <div 
                  v-for="contact in filteredContacts" 
                  :key="contact.id"
                  class="contact-row"
                >
                  <div class="form-group">
                    <p>Contact:</p>
                    <input 
                      :value="contact.contact_name"
                      type="text" 
                      class="form-input"
                      readonly
                      disabled
                    />
                  </div>
                  
                  <div class="form-group">
                    <p>Email:</p>
                    <div class="email-input-container">
                      <input 
                        v-model="contact.email" 
                        @blur="validateContactEmailOnBlur(contact)"
                        type="email" 
                        class="form-input"
                        :class="{ 'error': hasEmailValidationError(contact.id) }"
                        placeholder="Enter email address"
                      />
                      <button 
                        v-if="contact.original && contact.original.email"
                        class="reset-button"
                        @click="resetContactEmail(contact)"
                        title="Reset to original email"
                      >
                        <div class="reset-icon"></div>
                      </button>
                      <div v-if="hasEmailValidationError(contact.id)" class="error-message">
                        {{ getEmailValidationError(contact.id) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts Tab -->
          <div v-if="activeTab === 'contacts'" class="tab-panel">
            <div class="contacts-header">
              <h4>Contacts</h4>
              <button 
                class="add-contact-btn"
                @click="addNewContact"
                :disabled="saving"
              >
                <div class="add-icon"></div>
                Add Contact
              </button>
            </div>

            <div v-if="loading" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading contacts...</p>
            </div>
            
            <div v-else class="contacts-list">
              <div v-if="editableContacts.length === 0" class="no-contacts">
                <p>No contacts available. Click "Add Contact" to create one.</p>
              </div>
              <div v-else>
                <div 
                  v-for="(contact, index) in editableContacts" 
                  :key="contact.id || `new-${index}`"
                  class="contact-row"
                >
                  <div class="form-group">
                    <p>Name:</p>
                    <input 
                      v-model="contact.name"
                      type="text" 
                      class="form-input"
                      placeholder="Enter contact name"
                    />
                  </div>
                  
                  <div class="form-group">
                    <p>Email:</p>
                    <div class="input-container">
                      <input 
                        v-model="contact.email" 
                        @blur="validateEditableContactEmailOnBlur(contact, index)"
                        type="email" 
                        class="form-input"
                        :class="{ 'error': hasEmailValidationError(contact.id || `new-${index}`) }"
                        placeholder="Enter email address"
                      />
                      <div v-if="hasEmailValidationError(contact.id || `new-${index}`)" class="error-message">
                        {{ getEmailValidationError(contact.id || `new-${index}`) }}
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    class="remove-contact-btn"
                    @click="removeEditableContact(index)"
                    title="Remove Contact"
                  >
                    <div class="remove-icon"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          class="btn btn-secondary" 
          @click="closeModal"
          :disabled="saving"
        >
          Cancel
        </button>
        <button 
          class="btn btn-primary" 
          @click="saveContact"
          :disabled="saving || !hasValidContacts"
        >
          <span v-if="saving">Saving...</span>
          <span v-else>Save</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import individualApi from "@/api/tradingPlatform/individual";

export default {
  name: "EditContactPopUp",
  props: {
    open: {
      type: Boolean,
      default: false
    },
    cargoId: {
      type: [String, Number],
      required: true
    },
    ownerId: {
      type: [String, Number],
      required: true
    },
    filterRanks: {
      type: Array,
      default: () => []
    },
    filterSizes: {
      type: Array,
      default: () => []
    },
    zoneId: {
      type: [String, Number],
      default: null
    },
    sizes: {
      type: Array,
      default: () => []
    },
    ownerName: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      contacts: [],
      saving: false,
      loading: false,
      originalContacts: [],
      activeTab: 'individuals', // Default to individuals tab
      editableContacts: [], // For the contacts tab
      emailValidationErrors: {} // Store email validation errors
    };
  },
  computed: {
    filteredContacts() {
      // Filter out contacts that don't match the current filters
      return this.contacts.filter(contact => !this.isContactFiltered(contact));
    },
    hasValidContacts() {
      if (this.activeTab === 'individuals') {
        // For individuals, we just need at least one contact and no email validation errors
        const hasContacts = this.filteredContacts.length > 0;
        const hasEmailErrors = this.filteredContacts.some(contact => 
          this.hasEmailValidationError(contact.id) || !this.validateEmail(contact.email)
        );
        return hasContacts && !hasEmailErrors;
      } else {
        // For contacts, we need at least one contact with a name and no email validation errors
        const hasValidNames = this.editableContacts.length === 0 || this.editableContacts.some(contact => 
          contact.name?.trim()
        );
        const hasEmailErrors = this.editableContacts.some(contact => {
          const contactKey = contact.id || `new-${this.editableContacts.indexOf(contact)}`;
          return this.hasEmailValidationError(contactKey) || !this.validateEmail(contact.email);
        });
        return hasValidNames && !hasEmailErrors;
      }
    }
  },
  watch: {
    open(newVal) {
      if (newVal && this.cargoId && this.ownerId) {
        console.log("Modal opened, loading contacts with:", {
          cargoId: this.cargoId,
          ownerId: this.ownerId
        });
        this.loadContacts();
      }
    },
    activeTab(newTab) {
      if (newTab === 'contacts') {
        this.loadContactsData();
      }
    },
    contacts: {
      handler() {
        if (this.activeTab === 'contacts') {
          this.initializeEditableContacts();
        }
      },
      deep: true
    }
  },
  async created() {
    console.log("EditContactPopUp created with props:", {
      cargoId: this.cargoId,
      ownerId: this.ownerId,
      open: this.open
    });
    if (this.cargoId && this.ownerId) {
      this.loadContacts();
    }
    // Initialize editable contacts
    this.initializeEditableContacts();
  },
  methods: {
    // Email validation method
    validateEmail(email) {
      if (!email || email.trim() === '') {
        return true; // Empty email is valid (optional field)
      }
      // More strict email validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const trimmedEmail = email.trim();
      
      // Additional checks
      if (trimmedEmail.length < 5) return false; // Too short
      if (trimmedEmail.length > 254) return false; // Too long
      if (trimmedEmail.includes('..')) return false; // Double dots
      if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) return false; // Starts/ends with dot
      
      return emailRegex.test(trimmedEmail);
    },

    // Get validation error for a specific contact
    getEmailValidationError(contactId) {
      return this.emailValidationErrors[contactId] || '';
    },

    // Check if email has validation error
    hasEmailValidationError(contactId) {
      const hasError = this.emailValidationErrors[contactId] && this.emailValidationErrors[contactId] !== '';
      console.log("hasEmailValidationError check:", {
        contactId,
        errorValue: this.emailValidationErrors[contactId],
        hasError
      });
      return hasError;
    },

    async loadContacts() {
      this.loading = true;
      try {
        console.log("Loading individuals with:", {
          cargoId: this.cargoId,
          ownerId: this.ownerId
        });
        
        if (!this.cargoId || !this.ownerId) {
          console.error("Missing required props:", {
            cargoId: this.cargoId,
            ownerId: this.ownerId
          });
          this.$emit("error", "Missing cargo ID or owner ID");
          return;
        }
        
        const response = await individualApi.getContactsByCargoAndOwner(this.cargoId, this.ownerId);
        
        console.log("INDIVIDUALS RESPONSE", response);

        if (response.data && response.data.items) {
          this.originalContacts = [...response.data.items];
          this.contacts = response.data.items
            .filter(individual => individual.disable !== 1)
            .map(individual => ({
              id: individual.id,
              contact_name: `${individual.first_name} ${individual.surname}`,
              email: individual.email || "",
              rank: individual.rank || null,
              size_id: individual.size_id || null,
              zone_id: individual.zone_id || null,
              owner_size_zone: individual.owner_size_zone || null,
              original: individual.original || null,
              isFiltered: this.isContactFiltered(individual)
            }));
          
        } else {
          this.contacts = [];
          this.originalContacts = [];
        }
      } catch (error) {
        console.error("Error loading individuals:", error);
        this.$emit("error", "Failed to load individuals");
        this.contacts = [];
        this.originalContacts = [];
      } finally {
        this.loading = false;
      }
    },

    isContactFiltered(individual) {
      // Check if contact matches current filters
      // Contact is filtered if it doesn't match any filter
      
      console.log("=== isContactFiltered DEBUG ===");
      console.log("Individual data:", individual);
      console.log("Current filters:", {
        filterRanks: this.filterRanks,
        filterSizes: this.filterSizes,
        zoneId: this.zoneId
      });
      
      const rank = individual.rank;
      const sizeId = individual.size_id;
      const zoneId = individual.zone_id;

      console.log("Individual values:", {
        rank,
        sizeId,
        zoneId,
        converted: {
          rankNum: Number(rank),
          sizeIdNum: Number(sizeId),
          zoneIdNum: Number(zoneId),
          currentZoneIdNum: Number(this.zoneId)
        }
      });

      // Check filters - individual is filtered if it doesn't match any filter
      // Convert to numbers for comparison to ensure type consistency
      const rankNum = Number(rank);
      const sizeIdNum = Number(sizeId);
      const zoneIdNum = Number(zoneId);
      const currentZoneIdNum = Number(this.zoneId);
      
      const isFilteredByRank = this.filterRanks.length > 0 && !this.filterRanks.includes(rankNum);
      const isFilteredBySize = this.filterSizes.length > 0 && !this.filterSizes.includes(sizeIdNum);
      const isFilteredByZone = this.zoneId && zoneId !== null && zoneIdNum !== currentZoneIdNum;

      const isFiltered = isFilteredByRank || isFilteredBySize || isFilteredByZone;

      console.log("Filter checks:", {
        isFilteredByRank,
        isFilteredBySize,
        isFilteredByZone,
        isFiltered
      });
      
      console.log("=== END isContactFiltered DEBUG ===");

      return isFiltered;
    },

    async loadContactsData() {
      this.loading = true;
      try {
        console.log("Loading contacts with:", {
          cargoId: this.cargoId,
          ownerId: this.ownerId
        });
        
        if (!this.cargoId || !this.ownerId) {
          console.error("Missing required props:", {
            cargoId: this.cargoId,
            ownerId: this.ownerId
          });
          this.$emit("error", "Missing cargo ID or owner ID");
          return;
        }
        
        // Використовуємо новий ендпоінт для контактів
        console.log("Loading contacts with token:", localStorage.token ? "Present" : "Missing");
        const response = await individualApi.getContactsByCargoAndOwner(this.cargoId, this.ownerId, 'contacts');
        
        console.log("CONTACTS RESPONSE", response);

        if (response.data && response.data.items) {
          this.editableContacts = response.data.items
            .filter(contact => contact.disable !== 1)
            .map(contact => ({
              id: contact.id,
              name: `${contact.first_name} ${contact.surname || ''}`.trim(),
              email: contact.email || "",
              group_email: contact.group_email || ""
            }));
        } else {
          this.editableContacts = [];
        }
      } catch (error) {
        console.error("Error loading contacts:", error);
        this.$emit("error", "Failed to load contacts");
        this.editableContacts = [];
      } finally {
        this.loading = false;
      }
    },
    
    
    async saveContact() {
      if (this.saving) return;
      
      // Validate all emails before saving
      let hasInvalidEmails = false;
      
      if (this.activeTab === 'individuals') {
        // Check individuals emails
        for (const contact of this.filteredContacts) {
          if (!this.validateEmail(contact.email)) {
            this.emailValidationErrors[contact.id] = 'Please enter a valid email address';
            hasInvalidEmails = true;
          }
        }
      } else {
        // Check contacts emails
        for (let i = 0; i < this.editableContacts.length; i++) {
          const contact = this.editableContacts[i];
          const contactKey = contact.id || `new-${i}`;
          if (!this.validateEmail(contact.email)) {
            this.emailValidationErrors[contactKey] = 'Please enter a valid email address';
            hasInvalidEmails = true;
          }
        }
      }
      
      if (hasInvalidEmails) {
        this.$emit("error", "Please fix email validation errors before saving");
        return;
      }
      
      this.saving = true;
      try {
        let validContacts = [];
        let individualsToUpdate = [];

        if (this.activeTab === 'individuals') {
          // Handle individuals tab - include all filtered contacts (email can be empty)
          validContacts = this.filteredContacts;
          
          if (validContacts.length === 0) {
            this.$emit("error", "Please add at least one contact");
            return;
          }
          
          // Prepare individuals data for API
          individualsToUpdate = validContacts.map(contact => {
            // Find original individual data to preserve other fields
            const originalIndividual = this.originalContacts.find(orig => orig.id === contact.id);
            
            return {
              id: contact.id,
              first_name: originalIndividual?.first_name || contact.contact_name.split(' ')[0] || '',
              surname: originalIndividual?.surname || contact.contact_name.split(' ').slice(1).join(' ') || '',
              email: contact.email.trim(),
              group_email: originalIndividual?.group_email || '',
              disable: originalIndividual?.disable || 0
            };
          });
        } else {
          // Handle contacts tab - require only name, email can be empty
          validContacts = this.editableContacts.filter(contact => 
            contact.name?.trim()
          );
          
          // Allow saving even if no contacts (to clear all contacts)
          if (validContacts.length === 0 && this.editableContacts.length > 0) {
            this.$emit("error", "Please add at least one valid contact with name");
            return;
          }
          
          // Separate new contacts from existing ones
          const newContacts = validContacts.filter(contact => !contact.id);
          const existingContacts = validContacts.filter(contact => contact.id);
          
          // Prepare new contacts data for POST API
          const newContactsData = newContacts.map(contact => {
            const nameParts = contact.name.trim().split(' ');
            const emailValue = contact.email ? contact.email.trim() : '';
            console.log("New contact email processing:", {
              original: contact.email,
              trimmed: emailValue,
              isEmpty: emailValue === ''
            });
            return {
              first_name: nameParts[0] || '',
              surname: nameParts.slice(1).join(' ') || null,
              email: emailValue,
              group_email: contact.group_email || '',
              disable: 0
            };
          });
          
          // Prepare existing contacts data for PUT API
          const existingContactsData = existingContacts.map(contact => {
            const nameParts = contact.name.trim().split(' ');
            const emailValue = contact.email ? contact.email.trim() : '';
            console.log("Existing contact email processing:", {
              id: contact.id,
              original: contact.email,
              trimmed: emailValue,
              isEmpty: emailValue === ''
            });
            return {
              id: contact.id,
              first_name: nameParts[0] || '',
              surname: nameParts.slice(1).join(' ') || null,
              email: emailValue,
              group_email: contact.group_email || '',
              disable: 0
            };
          });
          
          console.log("Prepared contacts data for API:", {
            newContacts: newContactsData,
            existingContacts: existingContactsData
          });
          
          // Send data to appropriate APIs
          let createResponse = null;
          let updateResponse = null;
          
          if (newContactsData.length > 0) {
            createResponse = await individualApi.createContacts(this.cargoId, this.ownerId, newContactsData);
            console.log("Create contacts response:", createResponse);
          }
          
          if (existingContactsData.length > 0) {
            updateResponse = await individualApi.updateContacts(this.cargoId, this.ownerId, existingContactsData);
            console.log("Update contacts response:", updateResponse);
          }
          
          // Use the last response for consistency
          updateResponse = createResponse || updateResponse;
        }
        
        console.log("Prepared individuals data for API:", individualsToUpdate);
        console.log("Sending to API:", {
          cargoId: this.cargoId,
          ownerId: this.ownerId,
          individuals: individualsToUpdate
        });
        
        // Send data based on active tab
        let updateResponse;
        if (this.activeTab === 'individuals') {
          updateResponse = await individualApi.updateIndividuals(this.cargoId, this.ownerId, individualsToUpdate);
        }
        console.log("API response:", updateResponse);
        
        console.log("=== SAVE COMPLETED ===");
        console.log("Final valid contacts:", validContacts);
        console.log("Emitting contactUpdated event with data:", {
          cargoId: this.cargoId,
          ownerId: this.ownerId,
          contacts: validContacts
        });
        
        this.$emit("contactUpdated", {
          cargoId: this.cargoId,
          ownerId: this.ownerId,
          contacts: validContacts
        });
        this.closeModal();
      } catch (error) {
        console.error("Error saving contacts:", error);
        this.$emit("error", "Failed to save contacts");
      } finally {
        this.saving = false;
      }
    },
    
    closeModal() {
      this.$emit("showStatus", false);
    },

    // Methods for Contacts tab
    addNewContact() {
      const newContact = {
        id: null,
        name: '',
        email: ''
      };
      this.editableContacts.push(newContact);
      
      // Initialize validation for the new contact
      const contactKey = `new-${this.editableContacts.length - 1}`;
      this.emailValidationErrors[contactKey] = '';
    },

    async removeEditableContact(index) {
      const contactToRemove = this.editableContacts[index];
      
      // If it's an existing contact (has ID), we need to delete it from server
      if (contactToRemove.id) {
        try {
          await individualApi.deleteContacts(this.cargoId, this.ownerId, [contactToRemove.id]);
          console.log("Contact deleted from server:", contactToRemove.id);
        } catch (error) {
          console.error("Error deleting contact from server:", error);
          this.$emit("error", "Failed to delete contact from server");
          return; // Don't remove from UI if server deletion failed
        }
      }
      
      // Clear validation error for this contact
      const contactKey = contactToRemove.id || `new-${index}`;
      delete this.emailValidationErrors[contactKey];
      
      // Remove from UI
      this.editableContacts.splice(index, 1);
    },

    // Initialize editable contacts from individuals when switching to contacts tab
    initializeEditableContacts() {
      this.editableContacts = this.contacts.map(contact => ({
        id: contact.id,
        name: contact.contact_name,
        email: contact.email || ''
      }));
      
      // Clear validation errors for contacts tab
      Object.keys(this.emailValidationErrors).forEach(key => {
        if (key.startsWith('new-')) {
          delete this.emailValidationErrors[key];
        }
      });
    },

    // Validate contact email on blur
    validateContactEmailOnBlur(contact) {
      console.log("Validating contact email on blur:", {
        contactId: contact.id,
        email: contact.email
      });
      
      if (!contact.email || contact.email.trim() === '') {
        // Empty email is valid (optional field)
        this.emailValidationErrors[contact.id] = '';
      } else if (!this.validateEmail(contact.email)) {
        this.emailValidationErrors[contact.id] = 'Please enter a valid email address';
      } else {
        this.emailValidationErrors[contact.id] = '';
      }
    },

    // Reset contact email to original value
    resetContactEmail(contact) {
      if (contact.original && contact.original.email) {
        console.log("Resetting contact email:", {
          contactId: contact.id,
          oldValue: contact.email,
          newValue: contact.original.email
        });
        contact.email = contact.original.email;
        // Clear validation error when resetting
        this.emailValidationErrors[contact.id] = '';
      }
    },

    // Validate editable contact email on blur
    validateEditableContactEmailOnBlur(contact, index) {
      console.log("Validating editable contact email on blur:", {
        contactId: contact.id,
        email: contact.email,
        index: index
      });
      
      const contactKey = contact.id || `new-${index}`;
      console.log("Contact key:", contactKey);
      
      if (!contact.email || contact.email.trim() === '') {
        // Empty email is valid (optional field)
        console.log("Empty email - clearing error");
        this.emailValidationErrors[contactKey] = '';
      } else if (!this.validateEmail(contact.email)) {
        console.log("Invalid email - setting error");
        this.emailValidationErrors[contactKey] = 'Please enter a valid email address';
      } else {
        console.log("Valid email - clearing error");
        this.emailValidationErrors[contactKey] = '';
      }
      
      console.log("Email validation errors after update:", this.emailValidationErrors);
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  min-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  border-radius: 12px 12px 0 0;
}

.modal-header h3 {
  margin: 0;
  color: #2d3757;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.modal-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.owner-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.owner-label {
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
}

.owner-name {
  color: #2d3757;
  font-size: 14px;
  font-weight: 600;
}

.close-button {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background: #e9ecef;
  border-color: #dee2e6;
  transform: scale(1.05);
}

.close-icon {
  width: 16px;
  height: 16px;
  background: url(@/assets/close.svg) no-repeat center center;
  background-size: contain;
}

.modal-body {
  padding: 20px;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  border-bottom: 2px solid #e1e8ed;
  margin-bottom: 20px;
  background: #f8fafb;
  border-radius: 8px 8px 0 0;
  padding: 0;
}

.tab-button {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
  position: relative;
}

.tab-button:hover {
  background: #f1f5f9;
  color: #374151;
}

.tab-button.active {
  color: #2d3757;
  background: white;
  border-bottom-color: #2d3757;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}

.tab-content {
  min-height: 400px;
}

.tab-panel {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.contacts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.contacts-header h4 {
  margin: 0;
  color: #2d3757;
  font-size: 18px;
  font-weight: 600;
}


.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2d3757;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.contacts-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
}

.contacts-list::-webkit-scrollbar {
  width: 6px;
}

.contacts-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.contacts-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.contacts-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.no-contacts {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
  font-style: italic;
}

.contact-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  align-items: end;
  transition: all 0.2s ease;
  position: relative;
}

.contact-row:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #d0d7de;
}

.contact-row .form-group {
  flex: 1; /* take equal space */
  min-width: 0; /* allow shrinking */
  margin-bottom: 0;
}

.contact-row .form-group p {
  color: #4a5568;
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 14px;
  margin-top: 0;
}

.contact-row .form-input {
  width: 90% !important; /* override general form-input width */
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #ffffff;
  min-width: 0; /* prevent overflow in flex containers */
  flex: 1; /* take available space in flex container */
}

.contact-row .form-input:focus {
  border-color: #2d3757;
  box-shadow: 0 0 0 3px rgba(45, 55, 87, 0.1);
  outline: none;
}

.contact-row .form-input:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
}

.add-contact-btn {
  background: linear-gradient(135deg, #2d3757 0%, #3d4a6b 100%);
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(45, 55, 87, 0.2);
}

.add-contact-btn:hover {
  background: linear-gradient(135deg, #1e2a3f 0%, #2d3757 100%);
  box-shadow: 0 4px 8px rgba(45, 55, 87, 0.3);
  transform: translateY(-1px);
}

.add-icon {
  width: 18px;
  height: 18px;
  background: url(@/assets/Add_ring.svg) no-repeat center center;
  background-size: contain;
  filter: brightness(0) invert(1);
}

.remove-contact-btn {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  align-self: end; /* align with inputs baseline */
  margin: 0; /* grid gap handles spacing */
}

.remove-contact-btn:hover {
  background: #fecaca;
  border-color: #f87171;
  transform: scale(1.05);
}

.remove-icon {
  width: 16px;
  height: 16px;
  background: url(@/assets/close.svg) no-repeat center center;
  background-size: contain;
  filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%);
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4a5568;
  font-size: 14px;
}

.form-select,
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: "Lato", sans-serif;
  transition: all 0.2s ease;
  background: #ffffff;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: #2d3757;
  box-shadow: 0 0 0 3px rgba(45, 55, 87, 0.1);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px;
  border-top: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 0 0 12px 12px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 100px;
  position: relative;
  overflow: hidden;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #ced4da;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-primary {
  background: linear-gradient(135deg, #2d3757 0%, #3d4a6b 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(45, 55, 87, 0.2);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1e2a3f 0%, #2d3757 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(45, 55, 87, 0.3);
}

/* Responsive design */
@media screen and (max-width: 600px) {
  .modal-content {
    min-width: 300px;
    max-width: 95%;
  }
  
  .contact-row {
    grid-template-columns: 1fr auto; /* name | remove */
    gap: 12px;
  }
  
  .contact-row .form-group:nth-child(2) {
    display: none; /* Hide email on mobile, show only name and remove button */
  }
  
  .contact-row .form-group {
    margin-bottom: 0;
    min-width: auto;
  }
  
  .remove-contact-btn {
    align-self: flex-start;
    margin-top: 0;
  }
  
  .contacts-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}

/* Reset button styles */
.email-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

/* Input container for absolute positioned error messages */
.input-container {
  position: relative;
  display: flex;
  flex-direction: column;
}

.reset-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #2d3757;
  border: none;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  z-index: 1;
}

.reset-button:hover {
  background: #1e2a3f;
}

.reset-icon {
  width: 16px;
  height: 16px;
  background-color: white;
  mask: url(@/assets/sync_icon.svg) no-repeat center center;
  mask-size: contain;
  -webkit-mask: url(@/assets/sync_icon.svg) no-repeat center center;
  -webkit-mask-size: contain;
}

.form-input {
  padding-right: 40px; /* Make room for reset button */
}

/* Email validation error styles */
.form-input.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.error-message {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  background: white;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  border: 1px solid #fecaca;
}

</style>
